/* eslint-disable react/jsx-no-duplicate-props */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Autocomplete, CircularProgress, MenuItem, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClientOption from 'components/shared/options/ClientOption';
import { membershipStatus } from 'common/constants/options';
import StyledTextField from 'common/theme/fields/StyledTextfield';

const getRenderedOption = (option, props, renderType) => {
  const options = {
    client: () => <ClientOption option={option} props={props} key={option.value} />,
    default: () => (
      <MenuItem key={option.value} {...props}>
        {option.label}
      </MenuItem>
    )
  };

  return options[renderType]();
};

const isOptionDisable = (option, disableType) => {
  const options = {
    client: option.membership_status !== membershipStatus.active || (option.available_sessions <= 0 && (option.personalized_sessions ?? 0) <= 0),
    default: false
  };

  return options[disableType];
};

function TypeaheadFieldAdapter({
  disableType = 'default',
  input,
  isMultiple,
  loading = false,
  meta,
  onClose = () => {},
  renderInputProps,
  renderOptionAs = 'default',
  ...rest
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const size = useMemo(() => (isDesktop ? 'medium' : 'small'), [isDesktop]);

  const onBlur = (event) => {
    input.onBlur(event);
  };

  const onChange = (event, value) => {
    input.onChange(value);
  };

  const onFocus = (event) => {
    input.onFocus(event);
  };

  return (
    <Autocomplete
      {...rest}
      autoHighlight
      filterOptions={(options) => {
        let currentValues = [];

        if (Array.isArray(input.value)) {
          currentValues = input.value.filter(Boolean).map((item) => item.value);
        } else if (input.value && typeof input.value === 'object') {
          currentValues = [input.value.value];
        }

        return options.filter(Boolean).filter((option) => !currentValues.includes(option.value));
      }}
      getOptionDisabled={(option) => isOptionDisable(option, disableType)}
      getOptionLabel={(option) =>
        option && typeof option.label !== 'undefined' ? option.label : ''
      }
      isOptionEqualToValue={(option, value) => option.value === value.value || value === ''}
      multiple={isMultiple}
      noOptionsText={
        loading ? t('loadingOptions', { ns: 'common' }) : t('noOptions', { ns: 'common' })
      }
      onBlur={onBlur}
      onChange={onChange}
      onClose={onClose}
      onFocus={onFocus}
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'surface.content',
            maxHeight: '400px !important',
            '& .MuiAutocomplete-listbox': {
              '& .MuiAutocomplete-option': {
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'rgba(249, 189, 78, 0.1)'
                },
                '&.Mui-focused': {
                  bgcolor: 'rgba(249, 189, 78, 0.1)'
                },
                '&[aria-selected="true"]': {
                  bgcolor: 'rgba(249, 189, 78, 0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(249, 189, 78, 0.3)'
                  },
                  '&.Mui-focused': {
                    bgcolor: 'rgba(249, 189, 78, 0.3)'
                  }
                }
              }
            }
          }
        }
      }}
      componentsProps={{
        popper: {
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'flip',
              enabled: false
            }
          ]
        }
      }}
      sx={{
        // El borde estaba fijo en blanco translúcido (invisible sobre la card blanca del
        // filtro). Mode-aware: NEGRO marcado en claro, gris claro en oscuro. Icono dorado.
        '& .MuiOutlinedInput-root': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor:
              theme.palette.mode === 'light' ? 'rgba(17, 21, 27, 0.6)' : 'rgba(255, 255, 255, 0.3)'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.main
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.main
          }
        },
        '& .MuiSvgIcon-root': {
          color: theme.palette.secondary.main
        }
      }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          {...renderInputProps}
          InputProps={{
            ...params.InputProps,
            endAdornment:
              loading && meta.active ? (
                <CircularProgress color="secondary" size={25} />
              ) : (
                params.InputProps.endAdornment
              )
          }}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off'
          }}
        />
      )}
      renderOption={(props, option) => getRenderedOption(option, props, renderOptionAs)}
      size={size}
      value={input.value || null}
    />
  );
}

TypeaheadFieldAdapter.propTypes = {
  disableType: PropTypes.string,
  input: PropTypes.object.isRequired,
  isMultiple: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  meta: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  renderInputProps: PropTypes.object.isRequired,
  renderOptionAs: PropTypes.string
};

export default TypeaheadFieldAdapter;
